import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, of } from 'rxjs';
import { ClientRegisteredVendorForm } from 'src/app/classes/form-groups/client-registered-vendor-form';
import { ContactForm } from 'src/app/classes/form-groups/contact-form';
import {
  ClientRegisteredVendor,
  CompanyComplianceType,
  CompanyVendor
} from 'src/app/classes/models';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { ClientRegisteredVendorService } from 'src/app/services/client-registered-vendor.service';
import { CompanyService } from 'src/app/services/company.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { vendorNameLengthModalInputs } from 'src/app/validators/vendor-name-length-validator';

@Component({
  selector: 'app-add-edit-client-registered-vendor-modal',
  templateUrl: './add-edit-client-registered-vendor-modal.component.html',
  styleUrl: './add-edit-client-registered-vendor-modal.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AddEditClientRegisteredVendorModalComponent implements OnInit {
  constructor(
    private activeModal: NgbActiveModal,
    private ToastService: ToastService,
    private CompanyService: CompanyService,
    private UtilityService: UtilityService,
    private ClientRegisteredVendorService: ClientRegisteredVendorService,
    private ModalService: NgbModal
  ) {
    this.ClientRegisteredVendorForm.addControl(
      'CompanyComplianceType',
      new FormControl<CompanyComplianceType | null | undefined>(null, {
        validators: [Validators.required]
      })
    );
    this.ClientRegisteredVendorForm.addControl(
      'forceTempActive',
      new FormControl<boolean>(false)
    );
    this.ClientRegisteredVendorForm.addControl(
      'forceTempActiveTime',
      new FormControl<number>(0)
    );

    this.ClientRegisteredVendorForm.get(
      'forceTempActive'
    )?.valueChanges.subscribe({
      next: (forceTempActive) => {
        const ForceTempActiveTimeControl = this.ClientRegisteredVendorForm.get(
          'forceTempActiveTime'
        );

        if (forceTempActive) {
          if (ForceTempActiveTimeControl) {
            ForceTempActiveTimeControl.setValidators([Validators.required]);
            ForceTempActiveTimeControl.updateValueAndValidity();
          }
        } else {
          if (ForceTempActiveTimeControl) {
            ForceTempActiveTimeControl.removeValidators([Validators.required]);
            ForceTempActiveTimeControl.updateValueAndValidity();
          }
        }
      }
    });

    const StreetAddressForm =
      this.ClientRegisteredVendorForm.controls.StreetAddress;
    const MailingAddressForm =
      this.ClientRegisteredVendorForm.controls.MailingAddress;
    ['address1', 'city', 'state', 'zip'].forEach((controlName) => {
      const StreetAddressFormControl = StreetAddressForm.get(controlName);
      const MailingAddressFormControl = MailingAddressForm.get(controlName);

      if (StreetAddressFormControl) {
        StreetAddressFormControl.addValidators([Validators.required]);
        StreetAddressFormControl.updateValueAndValidity();
      }

      if (MailingAddressFormControl) {
        MailingAddressFormControl.addValidators([Validators.required]);
        MailingAddressFormControl.updateValueAndValidity();
      }
    });
  }

  @Input() ClientRegisteredVendor?: Partial<ClientRegisteredVendor> & {
    hasDuplicateInfo?: boolean;
    missingRequiredData?: boolean;
  };
  @Input() CompanyComplianceType?: CompanyComplianceType;
  @Input() CompanyId?: number;
  @Input() country: 'US' | 'CA' = 'US';

  ClientRegisteredVendorForm: ClientRegisteredVendorForm =
    new ClientRegisteredVendorForm();
  CompanyComplianceTypes: CompanyComplianceType[] = [];

  einMask = this.UtilityService.einMask;
  ssnMask = this.UtilityService.ssnMask;
  phoneMask = this.UtilityService.phoneMask;

  TaxIdentificationOptions: string[] = ['EIN', 'SSN'];
  ForceActiveTimeOptions: number[] = [0, 7, 30, 60, 90, 120];

  close(reason?: string) {
    this.activeModal.dismiss(reason);
  }

  save() {
    if (!this.ClientRegisteredVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing vendor information.',
        { returnError: true }
      );
    }

    // Only fields the admin changed: a row mapped from the client's AP system
    // keeps the AP names, which the server accepts at any length.
    const { legalName, dba } = this.ClientRegisteredVendorForm.controls;
    const nameLengthModalInputs = vendorNameLengthModalInputs({
      legalName: legalName.dirty ? legalName.value : undefined,
      dba: dba.dirty ? dba.value : undefined
    });
    if (nameLengthModalInputs) {
      const { componentInstance } = this.ModalService.open(
        ConfirmModalComponent,
        { centered: true }
      );
      Object.assign(componentInstance, nameLengthModalInputs);
      return;
    }

    forkJoin([
      of(
        this.UtilityService.generateValidationErrorsObject(
          this.ClientRegisteredVendorForm
        )
      ),
      this.ClientRegisteredVendorService.checkForDuplicateTaxId(
        this.ClientRegisteredVendorForm.value
      )
    ]).subscribe({
      next: ([formErrors, vendorsDuplicateTaxIds]) => {
        if (formErrors) {
          throw this.ToastService.addErrorToast(
            'Error',
            'There is an error in the form below.',
            { returnError: true }
          );
        }

        Object.assign(this.ClientRegisteredVendor!, {
          missingRequiredData: false
        });

        if (vendorsDuplicateTaxIds.length > 0) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Duplicate vendor tax ID found.',
            { returnError: true }
          );
        }

        Object.assign(this.ClientRegisteredVendor!, {
          hasDuplicateInfo: false
        });

        this.activeModal.close(this.ClientRegisteredVendor);
      }
    });
  }

  ngOnInit(): void {
    if (!this.ClientRegisteredVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing ClientRegisteredVendor record.',
        { returnError: true }
      );
    }

    this.ClientRegisteredVendorForm.patchValue(this.ClientRegisteredVendor);

    this.CompanyService.getCompanyComplianceTypes(
      this.UtilityService.stringifyObjectsInQueryObject({
        attributes: ['id', 'type'],
        CompanyId: this.CompanyId
      })
    ).subscribe({
      next: (types) => {
        this.CompanyComplianceTypes = types;
      }
    });

    const { CompanyVendors, Contacts } = this.ClientRegisteredVendor;

    if (CompanyVendors && CompanyVendors.length > 0) {
      const CompanyVendor = CompanyVendors[0];
      const { forceTempActive, forceTempActiveTime, CompanyComplianceType } =
        CompanyVendor as Partial<CompanyVendor> & {
          forceTempActiveTime?: number;
        };

      this.ClientRegisteredVendorForm.patchValue(
        Object.assign(this.ClientRegisteredVendor, {
          CompanyComplianceType: CompanyComplianceType
            ? CompanyComplianceType
            : null,
          forceTempActiveTime,
          forceTempActive
        })
      );
    }

    if (Contacts && Contacts.length > 0) {
      Contacts.forEach((contact) => {
        const contactForm = new ContactForm();
        contactForm.patchValue(contact);

        ['firstName', 'lastName', 'email', 'phone1'].forEach((controlName) => {
          const control = contactForm.get(controlName);

          if (control) {
            control.removeValidators([Validators.required]);
            control.updateValueAndValidity();
          }
        });

        this.ClientRegisteredVendorForm.controls.Contacts.push(contactForm);
      });
    }

    this.ClientRegisteredVendorForm.valueChanges.subscribe({
      next: (changes) => {
        const { CompanyComplianceType, forceTempActive, forceTempActiveTime } =
          changes as Partial<ClientRegisteredVendor> & {
            CompanyComplianceType: CompanyComplianceType;
            forceTempActiveTime: number;
            forceTempActive: boolean;
          };

        const CompanyVendorRecord: Partial<CompanyVendor> & {
          forceTempActiveTime?: number;
        } = {
          CompanyComplianceType,
          forceTempActive,
          forceTempActiveTime
        };

        Object.assign(this.ClientRegisteredVendor!, {
          ...changes,
          CompanyVendors: [CompanyVendorRecord]
        });
      }
    });
  }
}
